import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { getSlateColorToken } from "@faire/retailer-shared/SlateMappings/getSlateColorToken";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { ColorTokenKey } from "@faire/web-api--source/indigofair/data/ColorTokenKey";
import { IComponent } from "@faire/web-api--source/indigofair/data/IComponent";
import classNames from "classnames";
import React, { useMemo } from "react";
import { getContentWidthCss } from "@faire/retailer/components/SDUI/__internal__/Styling/DEPRECATED_componentContentWidth";
import { getFallbackHorizonalSpacingPixels } from "@faire/retailer/components/SDUI/__internal__/Styling/horizontalPadding";
import { getConsolidatedSpacingValues, getResponsiveSpacingPixels } from "@faire/retailer/components/SDUI/__internal__/Styling/spacing";
import { ComponentLoggingEventParams } from "@faire/retailer/components/SDUI/__internal__/types";
import { SDUIViewportHandler } from "@faire/retailer/components/SDUI/__internal__/Viewport/SDUIViewportHandler";
import { getSettingSDUITestModeEnabled } from "@faire/retailer/settings/getSettingSDUITestModeEnabled";
import { getResponsiveStyles } from "../Styling/responsiveValues";
import { SDUIComponentClientCodeHandler } from "./__internal__/SDUIComponentClientCodeHandler";
import * as componentStyles from "./styles/styles.css";
var VISIBILITY_THRESHOLD = {
    desktopAndAbove: -900,
    tabletAndAbove: -800,
    mobileAndAbove: -700
};
export var SDUIComponentRenderer = function(param) {
    var component = param.component, componentRenderers = param.componentRenderers, sectionLoggingParams = param.sectionLoggingParams, isPrimaryContent = param.isPrimaryContent, renderBehaviourOverrides = param.renderBehaviourOverrides, disableViewEvent = param.disableViewEvent, loadTimingRef = param.loadTimingRef, onClickClientDismissHandler = param.onClickClientDismissHandler;
    var loggingEventParams = useMemo(function() {
        return ComponentLoggingEventParams.build(_object_spread_props(_object_spread({}, sectionLoggingParams, component.logging_data), {
            component_id: component.id
        }));
    }, [
        sectionLoggingParams,
        component
    ]);
    var ComponentLoadingStateRenderer = getComponentRenderer(component, componentRenderers, "pending");
    var shouldRenderLoadingState = ComponentLoadingStateRenderer !== undefined && !isPrimaryContent;
    if (shouldRenderLoadingState) {
        var _component_style;
        return /*#__PURE__*/ _jsx(WhenVisibilityIsImminent, {
            customizePlaceholder: /*#__PURE__*/ _jsx("div", {
                className: classNames(componentStyles.fullWidth, componentStyles.componentContentCentering),
                style: getContentWidthCss((_component_style = component.style) === null || _component_style === void 0 ? void 0 : _component_style.content_width, loggingEventParams),
                children: /*#__PURE__*/ _jsx(ComponentLoadingStateRenderer, {
                    component: component,
                    loggingData: loggingEventParams
                })
            }),
            threshold: VISIBILITY_THRESHOLD,
            useTransition: true,
            shouldRunNormallyInTest: true,
            children: /*#__PURE__*/ _jsx(SDUIComponentRendererBase, {
                component: component,
                componentRenderers: componentRenderers,
                disableViewEvent: disableViewEvent,
                loggingData: loggingEventParams,
                isPrimaryContent: isPrimaryContent,
                renderBehaviourOverrides: renderBehaviourOverrides,
                loadTimingRef: loadTimingRef
            })
        });
    } else {
        return /*#__PURE__*/ _jsx(SDUIComponentRendererBase, {
            component: component,
            componentRenderers: componentRenderers,
            isPrimaryContent: isPrimaryContent,
            loadTimingRef: loadTimingRef,
            loggingData: loggingEventParams,
            disableViewEvent: disableViewEvent,
            renderBehaviourOverrides: renderBehaviourOverrides,
            onClickClientDismissHandler: onClickClientDismissHandler
        });
    }
};
var SDUIComponentRendererBase = function(param) {
    var component = param.component, componentRenderers = param.componentRenderers, loggingData = param.loggingData, isPrimaryContent = param.isPrimaryContent, disableViewEvent = param.disableViewEvent, renderBehaviourOverrides = param.renderBehaviourOverrides, loadTimingRef = param.loadTimingRef, onClickClientDismissHandler = param.onClickClientDismissHandler;
    var _component_style;
    var ComponentRenderer = getComponentRenderer(component, componentRenderers);
    if (!ComponentRenderer) {
        return null;
    }
    // Needed to prevent logging of onView events for spacer components
    var isSpacerElement = component.spacer_component !== undefined;
    var renderBehaviour = _object_spread({
        imagesLoadingStrategy: isPrimaryContent ? "eager" : undefined,
        deferInitializationUntilView: !isPrimaryContent
    }, renderBehaviourOverrides);
    var componentLevelSpacing = getComponentLevelSpacing(component, loggingData);
    return /*#__PURE__*/ _jsx(SDUIViewportHandler, {
        viewports: component.viewports,
        children: /*#__PURE__*/ _jsx(SDUIComponentBackgroundColor, {
            color: (_component_style = component.style) === null || _component_style === void 0 ? void 0 : _component_style.background_color,
            children: /*#__PURE__*/ _jsx(SDUIComponentClientCodeHandler, {
                className: componentLevelSpacing.className,
                componentStyle: componentLevelSpacing.styles,
                loggingData: loggingData,
                onViewActions: component.on_view,
                ref: loadTimingRef,
                isRequiredForInteractivity: isPrimaryContent,
                logSDUIViewEvent: !isSpacerElement && !disableViewEvent,
                logLegacyViewEvent: component.banner_component !== undefined && !disableViewEvent,
                children: /*#__PURE__*/ _jsx(ComponentRenderer, {
                    component: component,
                    loggingData: loggingData,
                    renderBehaviour: renderBehaviour,
                    onClickClientDismissHandler: onClickClientDismissHandler
                })
            })
        })
    });
};
var SDUIComponentBackgroundColor = function(param) {
    var color = param.color, children = param.children;
    return /*#__PURE__*/ _jsx("div", {
        className: componentStyles.fullWidth,
        style: {
            backgroundColor: getSlateColorToken(color, ColorTokenKey.COLOR_TOKEN_KEY_UNKNOWN)
        },
        children: children
    });
};
var getComponentLevelSpacing = function(component, loggingData) {
    // To is needed for backwards compatible styling with LayoutElements
    var isLayoutElement = component.layout_element !== undefined;
    if (isLayoutElement) {
        return {
            className: classNames(componentStyles.fullWidth, getSettingSDUITestModeEnabled() ? componentStyles.testModeComponentBorder : undefined),
            styles: {}
        };
    } else {
        var _component_style;
        var _component_style1;
        var _ref = (_component_style1 = component.style) !== null && _component_style1 !== void 0 ? _component_style1 : IComponent.IComponentStyle.build(), top_spacing = _ref.top_spacing, responsive_top_spacing = _ref.responsive_top_spacing, bottom_spacing = _ref.bottom_spacing, responsive_bottom_spacing = _ref.responsive_bottom_spacing, horizontal_spacing = _ref.horizontal_spacing, responsive_horizontal_spacing = _ref.responsive_horizontal_spacing;
        var responsivePaddingTop = getResponsiveSpacingPixels(top_spacing, responsive_top_spacing);
        var responsivePaddingBottom = getResponsiveSpacingPixels(bottom_spacing, responsive_bottom_spacing);
        var responsiveHorizontalPadding = horizontal_spacing !== undefined || responsive_horizontal_spacing !== undefined ? getResponsiveSpacingPixels(horizontal_spacing, responsive_horizontal_spacing) : getFallbackHorizonalSpacingPixels(component, loggingData);
        var spacing = getConsolidatedSpacingValues(responsivePaddingTop, responsivePaddingBottom, responsiveHorizontalPadding, responsiveHorizontalPadding);
        var _getResponsiveStyles = getResponsiveStyles(spacing, {
            mobileAndAbove: [
                componentStyles.mobileAndAbovePadding,
                componentStyles.varMobilePadding
            ],
            tabletAndAbove: [
                componentStyles.tabletAndAbovePadding,
                componentStyles.varTabletPadding
            ],
            desktopAndAbove: [
                componentStyles.desktopAndAbovePadding,
                componentStyles.varDesktopPadding
            ]
        }), paddingClassnames = _getResponsiveStyles.className, paddingStyles = _getResponsiveStyles.style;
        return {
            className: classNames(componentStyles.fullWidth, componentStyles.componentContentCentering, paddingClassnames),
            styles: _object_spread({}, getContentWidthCss((_component_style = component.style) === null || _component_style === void 0 ? void 0 : _component_style.content_width, loggingData), paddingStyles)
        };
    }
};
var getComponentRenderer = function(component, componentRenderers) {
    var state = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : "active";
    if (component.layout_element) {
        var _componentRenderers_layout_element;
        return (_componentRenderers_layout_element = componentRenderers["layout_element"]) === null || _componentRenderers_layout_element === void 0 ? void 0 : _componentRenderers_layout_element[state];
    } else if (component.carousel_component) {
        var _componentRenderers_carousel_component;
        return (_componentRenderers_carousel_component = componentRenderers["carousel_component"]) === null || _componentRenderers_carousel_component === void 0 ? void 0 : _componentRenderers_carousel_component[state];
    } else if (component.banner_component) {
        var _componentRenderers_banner_component;
        return (_componentRenderers_banner_component = componentRenderers["banner_component"]) === null || _componentRenderers_banner_component === void 0 ? void 0 : _componentRenderers_banner_component[state];
    } else if (component.spacer_component) {
        var _componentRenderers_spacer_component;
        return (_componentRenderers_spacer_component = componentRenderers["spacer_component"]) === null || _componentRenderers_spacer_component === void 0 ? void 0 : _componentRenderers_spacer_component[state];
    } else if (component.header_component) {
        var _componentRenderers_header_component;
        return (_componentRenderers_header_component = componentRenderers["header_component"]) === null || _componentRenderers_header_component === void 0 ? void 0 : _componentRenderers_header_component[state];
    } else if (component.grid_component) {
        var _componentRenderers_grid_component;
        return (_componentRenderers_grid_component = componentRenderers["grid_component"]) === null || _componentRenderers_grid_component === void 0 ? void 0 : _componentRenderers_grid_component[state];
    } else if (component.collapsible_list_web_banner_component) {
        var _componentRenderers_collapsible_list_web_banner_component;
        return (_componentRenderers_collapsible_list_web_banner_component = componentRenderers["collapsible_list_web_banner_component"]) === null || _componentRenderers_collapsible_list_web_banner_component === void 0 ? void 0 : _componentRenderers_collapsible_list_web_banner_component[state];
    } else if (component.alert_banner_component) {
        var _componentRenderers_alert_banner_component;
        return (_componentRenderers_alert_banner_component = componentRenderers["alert_banner_component"]) === null || _componentRenderers_alert_banner_component === void 0 ? void 0 : _componentRenderers_alert_banner_component[state];
    } else if (component.site_banner_component) {
        var _componentRenderers_site_banner_component;
        return (_componentRenderers_site_banner_component = componentRenderers["site_banner_component"]) === null || _componentRenderers_site_banner_component === void 0 ? void 0 : _componentRenderers_site_banner_component[state];
    }
    return undefined;
};
